import { useMemo, useState } from "react";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";

// Order of attribute subThemes in the Attributes tab — matched by subTheme tagId.
// Anything not in this list goes after these in count-desc order.
const ATTRIBUTE_TAG_ID_ORDER = [
  "sentiment_polarity",
  "emotion_affect",
  "intent",
  "expression_mode",
  "epistemic_status",
  "discourse_role",
  "named_entities",
];

const getAttributeOrderIndex = (tagId) => {
  const idx = ATTRIBUTE_TAG_ID_ORDER.indexOf(tagId);
  return idx === -1 ? ATTRIBUTE_TAG_ID_ORDER.length : idx;
};

const TAXONOMY_TAB_OPTIONS = [
  { value: "thematic", display: "Thematics" },
  { value: "attribute", display: "Attributes" },
];

export const useAnnoExplorerTaxonomyTabs = (barGraphData) => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const annoMode = getAnnoMode(platformConfigs);
  // `enabled` = non-legacy LAYOUT (consumer swaps the legacy top bar-graph
  // for the right-side viz panel) — true for dual-agent AND split. The role
  // PARTITION below is dual-agent-only: split has no Thematics/Attributes
  // split, so its rows pass through unfiltered.
  const enabled = annoMode !== "legacy";
  const partitionByRole = annoMode === "dual-agent";

  const [taxonomyView, setTaxonomyView] = useState(TAXONOMY_TAB_OPTIONS[0].value);

  const filteredBarGraphData = useMemo(() => {
    if (!partitionByRole) return barGraphData;
    if (!Array.isArray(barGraphData)) return barGraphData;

    const isAttribute = (row) => row?.taxonomyRole === "attribute";
    if (taxonomyView === "attribute") {
      const attrRows = barGraphData.filter(isAttribute);
      // stable-sort by attribute category priority; within a category, preserve order
      return [...attrRows].sort(
        (a, b) => getAttributeOrderIndex(a.tagCategory) - getAttributeOrderIndex(b.tagCategory)
      );
    }
    return barGraphData.filter((row) => !isAttribute(row));
  }, [barGraphData, taxonomyView, partitionByRole]);

  return {
    enabled,
    tabOptions: TAXONOMY_TAB_OPTIONS,
    taxonomyView,
    setTaxonomyView,
    filteredBarGraphData,
  };
};
